import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { FormikHelpers } from 'formik';
import { useSetAtom } from 'jotai';
import { MessageForm, MessageFormValues } from '../../forms';
import { messageAtom } from '../../store';

import {
  ScreenLayout,
  FlowHeader,
  VerifiedResidentsLabel,
  ResidentCarousel,
  SuggestedMessages,
  MessageInput,
  RewardBanner,
  FooterActions,
  FooterMeta,
} from '../shared';

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.5rem 1rem;
`;

const StyledSectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #232323;
`;

const StyledSectionCopy = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: #232323;
`;

const StyledSuggestions = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 1rem 0.5rem 1rem;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export function MessagePage() {
  const navigate = useNavigate();
  const setMessage = useSetAtom(messageAtom);
  const listing = globalThis.xprops.listing;
  const residents = globalThis.xprops.residents;

  const suggestions = [
    { message: 'How do you like living here?', delay: '0.5s' },
    { message: 'Can you share why you chose to live here?', delay: '1.5s' },
    { message: 'What do you think about the amenities?', delay: '2.5s' },
  ];

  const handleFormSubmit = useCallback(
    async (
      values: MessageFormValues,
      helpers: FormikHelpers<MessageFormValues>
    ) => {
      try {
        setMessage(values.message);
        navigate('/auth/create');
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [navigate, setMessage]
  );

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  return (
    <ScreenLayout
      header={
        <FlowHeader title="Ask about their experience" onClose={handleClose} />
      }
      footer={
        <StyledFooter>
          <RewardBanner rewardAmount={listing.reward_amount} />
          <FooterActions screen={globalThis.xprops.screen} />
          <FooterMeta />
        </StyledFooter>
      }
    >
      <MessageForm onSubmit={handleFormSubmit}>
        <StyledBody style={{ position: 'relative' }}>
          <VerifiedResidentsLabel />
          <ResidentCarousel residents={residents} />

          <StyledSection>
            <StyledSectionTitle>Start a conversation</StyledSectionTitle>
            <StyledSectionCopy>
              Safely and privately message real residents to get authentic
              answers to your questions.
            </StyledSectionCopy>

            <MessageInput placeholder="Ask a question in your own words..." />
          </StyledSection>

          <StyledSuggestions>
            <SuggestedMessages suggestions={suggestions} />
          </StyledSuggestions>
        </StyledBody>
      </MessageForm>
    </ScreenLayout>
  );
}
